<script setup lang="ts">
import type { GradeCode } from '~/models/Grade'
import type { FederatedModuleMount } from '~/models/Brikke/FederatedModuleMount'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from '#imports'
import { findRelevantGradeType, sortByGradeIndex } from '~/utils/gradeSorter'
import { setFlytProgressLocally } from '~/utils/flyt'
import { waitFor } from '~/utils/asyncUtils'
import useAuthStore from '~/stores/auth'
import { GradeType } from '~/models/GradeType'
import { getAccessToken } from '~/api/client/oidcClient'
import AuCallout from '~/components/ui/AuCallout.vue'
import FeedbackButton from '~/components/feedback/FeedbackButton.vue'

const props = defineProps<{
  taskId: string
  progressId?: number
  theme?: string
  mode?: string
  assignment?: boolean
  grades?: GradeCode[]
}>()

const { t } = useI18n()
const route = useRoute()
const { selectedGradeType, userPreferredLanguage, isTeacher, isAuthenticated } = storeToRefs(useAuthStore())

const flytContainerRef = ref<HTMLElement | null>(null)
const failedToLoad = ref(false)
let cleanup: (() => void) | null = null

const gradeTypeViewMap: Partial<Record<GradeType, string>> = {
  [GradeType.Grade1To4]: 'lowerPrimary',
}

const gradeType = computed(() => {
  const contentGrades = [...new Set(props.grades)].sort(sortByGradeIndex)
  return findRelevantGradeType(contentGrades[0]) || selectedGradeType.value
})

const flytGradeType = computed(() => gradeType.value ? gradeTypeViewMap[gradeType.value] : '')

const loadTask = async () => {
  await waitFor(() => isAuthenticated.value, 2500)
  const el = flytContainerRef.value!
  const flytModule = await import('remote_app/flyt')
  cleanup = flytModule.default.mount(el as HTMLElement, {
    flytId: props.taskId,
    theme: 'aunivers',
    gradeType: flytGradeType.value,
    getAuthToken: () => getAccessToken(),
    endpoint: import.meta.env.VITE_FLYT_ENGINE,
    locale: `${userPreferredLanguage.value}-no`,
    ...(props.theme && { theme: props.theme }),
    ...(props.mode && { mode: props.mode }),
    ...(props.assignment && { assignmentId: `flyt_test-${props.taskId}` }),
    callbacks: {
      onErrorCaptured(e: Error) {
        console.error(e)
        failedToLoad.value = true
      },
      onProgress(totalItems: number, itemsAnswered: number) {
        if (route.params.taskId && props.progressId && !isTeacher.value) {
          return setFlytProgressLocally(props.progressId, totalItems, itemsAnswered)
        }
      },
    },
  })
  import('~/styles/fonts.css')
}

onMounted(() => {
  loadTask()
})

watch(props, () => {
  cleanup?.()
  loadTask()
})

onBeforeUnmount(() => {
  cleanup?.()
})
</script>

<template>
  <section class="motion-safe:animate-fade au-flyt mx-auto" data-ignore-tts>
    <div
      v-if="!failedToLoad"
      ref="flytContainerRef"
    />
    <div v-else>
      <AuCallout variant="error" :title="t('unknownError')" />
    </div>
    <div class="flex flex-row">
      <FeedbackButton
        v-if="isTeacher"
        size="large"
        classes="test"
      />
    </div>
  </section>
</template>

<style>
  .FlytApp {
  padding: 40px 0;
  border-radius: 20px;
}
</style>
